import React from 'react';

interface PalletFillVisualProps {
  fillPercent: number;
  minFill?: number;
}

const PalletFillVisual: React.FC<PalletFillVisualProps> = ({ fillPercent, minFill = 50 }) => {
  const clampedPercent = Math.min(Math.max(fillPercent, 0), 100);

  // Pallet dimensions
  const palletWidth = 200;
  const palletBaseHeight = 24;

  // Stack area dimensions
  const stackWidth = 180;
  const stackX = 10;

  // Fixed box layout
  const boxHeight = 30;
  const boxGap = 2;
  const maxBoxes = 5;
  const filledBoxes = Math.max(0, Math.round((clampedPercent / 100) * maxBoxes));

  // Color based on fill level (matches parent thresholds) — green once the target fill is reached, amber otherwise.
  const isFilledEnough = clampedPercent >= minFill;
  const boxColor = isFilledEnough ? '#4CAF50' : '#FF9800';
  const boxColorDark = isFilledEnough ? '#388E3C' : '#F57C00';

  // Fixed height - always show space for maxBoxes
  const maxStackHeight = maxBoxes * (boxHeight + boxGap);
  const totalHeight = maxStackHeight + palletBaseHeight + 30;
  const palletY = maxStackHeight + 20;
  const stackBaseY = palletY;

  return (
    <svg
      width="200"
      height={totalHeight}
      viewBox={`0 0 200 ${totalHeight}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Empty box outlines (unfilled slots) */}
      {Array.from({ length: maxBoxes }).map((_, i) => {
        const boxY = stackBaseY - (i + 1) * (boxHeight + boxGap);
        return (
          <rect
            key={`empty-${i}`}
            x={stackX}
            y={boxY}
            width={stackWidth}
            height={boxHeight}
            fill="transparent"
            stroke="#ddd"
            strokeWidth="1"
            strokeDasharray="4 2"
            rx="2"
          />
        );
      })}

      {/* Filled boxes from bottom up */}
      {Array.from({ length: filledBoxes }).map((_, i) => {
        const boxY = stackBaseY - (i + 1) * (boxHeight + boxGap);
        const bw = stackWidth;
        const bx = stackX;

        return (
          <g key={i}>
            {/* Box body */}
            <rect
              x={bx}
              y={boxY}
              width={bw}
              height={boxHeight}
              fill={boxColor}
              stroke={boxColorDark}
              strokeWidth="1.5"
              rx="2"
            />
            {/* Box tape/line detail */}
            <line
              x1={bx + bw / 2}
              y1={boxY}
              x2={bx + bw / 2}
              y2={boxY + boxHeight}
              stroke={boxColorDark}
              strokeWidth="1"
              opacity="0.5"
            />
          </g>
        );
      })}

      {/* Pallet base - wooden slats */}
      <rect
        x="0"
        y={palletY}
        width={palletWidth}
        height="6"
        fill="#A0734A"
        stroke="#7A5530"
        strokeWidth="1"
        rx="1"
      />
      <rect
        x="0"
        y={palletY + 8}
        width={palletWidth}
        height="6"
        fill="#A0734A"
        stroke="#7A5530"
        strokeWidth="1"
        rx="1"
      />
      <rect
        x="0"
        y={palletY + 16}
        width={palletWidth}
        height="6"
        fill="#A0734A"
        stroke="#7A5530"
        strokeWidth="1"
        rx="1"
      />

      {/* Pallet legs/blocks */}
      <rect
        x="10"
        y={palletY + 6}
        width="20"
        height="10"
        fill="#8B6340"
        stroke="#7A5530"
        strokeWidth="0.5"
      />
      <rect
        x="90"
        y={palletY + 6}
        width="20"
        height="10"
        fill="#8B6340"
        stroke="#7A5530"
        strokeWidth="0.5"
      />
      <rect
        x="170"
        y={palletY + 6}
        width="20"
        height="10"
        fill="#8B6340"
        stroke="#7A5530"
        strokeWidth="0.5"
      />

      {/* Fill percentage text */}
      <text
        x="100"
        y="14"
        textAnchor="middle"
        fontSize="14"
        fontWeight="bold"
        fill="#333"
      >
        {clampedPercent.toFixed(1)}%
      </text>
    </svg>
  );
};

export default PalletFillVisual;